<template>
	<div :class="'input-item ' + classList + ' ' + containerClasses" :style="styles">
		<label class="input-label" :for="id" v-if="label !== ''"
			>{{ label }} <span class="asterisk" v-if="asterisk">*</span>
			<icon type="help" class="not-mobile help-icon-for-field" v-if="help !== ''" :tooltip="help"></icon
		></label>
		<div class="input-field-block">
			<span @click="focusInput()" class="input-append input-prefix" v-if="has_prefix">{{ prefix }}</span>
			<input
        style="max-height: 33px"
				:tabindex="tabindex"
				@focus="focus"
				@blur="blur"
				@input="update($event)"
				@change="change($event)"
				@keypress.enter="keypressEnter($event)"
				@keydown.tab="keypressTab($event)"
				:type="type"
				:id="id"
				:name="name"
				:value="value"
				:step="step"
				:placeholder="placeholder"
				:style="inputStyles"
				:class="'input ' + classList + ' ' + extraClasses"
				:maxlength="maxlength"
				:max="max"
				:min="min"
				:autofocus="autofocus"
				:autocomplete="autocomplete"
				:disabled="disabled"
				:readonly="readonly"
				:required="required"
				:spellcheck="spellcheck"
				ref="input"
			/>
			<span @click="focusInput()" class="input-append input-suffix" v-if="has_suffix">{{ suffix }}</span>
			<icon-btn class="input-append input-suffix" v-if="icon" :type="icon" @clicked="iconClicked"></icon-btn>
		</div>
		<p class="input-help error" v-show="computedError" v-html="computedError"></p>
		<p class="input-help warning" v-if="warning" v-html="warning"></p>
		<p class="input-help only-mobile" v-if="help !== ''" v-html="help"></p>
	</div>
</template>

<script type="text/babel">
export default {
	props: {
		id: { default: "" },
		name: { default: "" },
		value: {},
		placeholder: { default: "" },

		label: { default: "" },
		prefix: { default: "" },
		suffix: { default: "" },
		help: { default: "" },
		error: { default: false },
		warning: { default: false },
		step: { default: null },
		tabindex: { default: null },

		preventEnter: { default: false },
		asterisk: { default: false },
		required: { default: false },
		disabled: { default: false },
		readonly: { default: false },
		autofocus: { default: false },
		spellcheck: { default: false },
		autocomplete: { default: "on" }, // on/off
		textDirection: { default: "left" }, // left,center,right
		maxlength: { default: false },
		max: { default: false },
		min: { default: false },

		icon: { default: null },

		hasError: { default: false },
		hasSuccess: { default: false },
		isLoading: { default: false },
		isFull: { default: false },
		isModel: { default: true },

		type: { default: "text" }, // default input types
		extraClasses: { default: "" },
		containerClasses: { default: "" },
		width: { default: null },
		size: { default: "normal" }, // narrow, normal, large
	},

	data: function () {
		return {
			generatedInputStyles: [],
			hasFocus: false,
			dynError: false,
		};
	},

	methods: {
		update(e) {
			this.$emit("input", e.target.value);
		},

		focusInput() {
			this.$refs.input.focus();
		},

		keypressEnter(e) {
			if (this.preventEnter) {
				e.preventDefault();
			}

			this.$emit("keypress.enter", e);
			this.$emit("enter", e);
		},

		keypressTab(e) {
			this.$emit("tab", e);
		},

		change(e) {
			this.$emit("change", e);
		},

		iconClicked(e) {
			this.$emit("icon-clicked", e);
		},

		focus() {
			this.hasFocus = true;
			this.$emit("focus");
		},

		blur() {
			this.hasFocus = false;
			this.$emit("blur");
		},

		validateEmail() {
			if (this.type === "email") {
				if (isValidEmail(this.value)) {
					this.dynError = false;
				} else {
					this.dynError = "Invalid Email";
				}
			}
		},
	},

	watch: {
		value: function (value) {
			if (this.type === "email" && value.length > 3) {
				this.validateEmail(value);
			}
		},
	},

	computed: {
		styles() {
			let style = {};

			if (this.width !== null) {
				style["width"] = this.width;
			}

			return style;
		},

		inputStyles() {
			let style = {};

			this.generatedInputStyles.forEach(function (item) {
				style[item.type] = item.value;
			});

			return style;
		},

		has_suffix() {
			return this.suffix && this.suffix !== "";
		},

		has_prefix() {
			return this.prefix && this.prefix !== "";
		},

		classList() {
			let classList = "";

			classList += " size-" + this.size;
			classList += " type-" + this.type;
			classList += " text-" + this.textDirection;

			if (this.has_suffix) {
				classList += " has-suffix";
			}

			if (this.has_prefix) {
				classList += " has-prefix";
			}

			if (this.label !== "") {
				classList += " has-label";
			}

			if (this.help !== "") {
				classList += " has-help";
			}

			if (this.hasSuccess) {
				classList += " has-success";
			}

			if (this.hasError || this.error || this.dynError) {
				classList += " has-error";
			}

			if (this.warning) {
				classList += " has-warning";
			}

			if (this.isLoading) {
				classList += " is-loading";
			}

			if (this.isFull) {
				classList += " is-full";
			}

			if (this.required) {
				classList += " is-required";
			}

			if (this.hasFocus) {
				classList += " has-focus";
			}

			return classList;
		},

		computedError() {
			return this.dynError || this.error;
		},
	},

	mounted() {
		this.$nextTick(() => {
			if (this.has_prefix) {
				const currentPaddingLeft =
					getComputedStyle(this.$el.querySelector(".input"))
						.getPropertyValue("padding-left")
						.replace("px", "") * 1;
				this.generatedInputStyles.push({
					type: "padding-left",
					value: this.$el.querySelector(".input-prefix").clientWidth + currentPaddingLeft + "px",
				});
			}

			if (this.has_suffix) {
				const currentPaddingRight =
					getComputedStyle(this.$el.querySelector(".input"))
						.getPropertyValue("padding-right")
						.replace("px", "") * 1;
				this.generatedInputStyles.push({
					type: "padding-right",
					value: this.$el.querySelector(".input-suffix").clientWidth + currentPaddingRight + "px",
				});
			}

			if (this.type === "email" && this.value?.length > 3) {
				this.validateEmail(this.value);
			}
		});
	},
};
</script>
